import { HeliosServerSideRequest } from '@helios/server-side-util'
import { Observable } from 'rxjs'
import { ExternalPositionDetailResponse } from '../model/external-position-detail.model'
import {
  HistoricalBalanceComparison,
  HistoricalBalanceComparisonExtToExt,
} from '../model/historical-balance-comparison.model'
import {
  PositionComparisons,
  PositionComparisonsExtToExt,
} from '../model/position-comparison.model'
import {
  ExportExcelRequest,
  Position,
  PositionAvailableRestrictedColumns,
  PositionsSetValues,
} from '../model/positions.model'

export interface PositionsRequestContext {
  filterContext: AdvancedFilterContext
}

export interface AdvancedFilterContext {
  includeClosedPositions?: boolean
  calcCashUsingEntryDate?: boolean
}

export interface AdvanceOptionsContext {
  onlyDifferences?: boolean
  onlyIncorrect?: boolean
}

export interface HistoricalBalanceComparisonContext {
  positionId: string
  onlyDifferences: boolean
  clearedOnly: boolean
  filterContext: {
    reconDate?: Date
  }
}

export interface PositionHistoryExtToExtRequest {
  positionIdSelected: string
  positionIdOtherDataSource: string
  clearedOnly: boolean
  onlyDifferences: boolean
}

export interface PositionHistoryRequest {
  positionId: string
  clearedOnly: boolean
  onlyDifferences: boolean
}

export interface ExternalPositionRequest {
  positionId: string
  clearedOnly: boolean
  includeValuationData: boolean
}

export interface ExternalPositionExtToExtRequest {
  positionIdSelected: string
  positionIdOtherDataSource: string
  clearedOnly: boolean
  includeValuationData: boolean
}

export interface ExternalPositionDetailRequest {
  positionId: number
}

export enum PositionsAvailableActions {
  TRANSFER_OUT_ALL_POSITIONS_DIRECT = 'transferOutAllPositionsDirect',
  TRANSFER_OUT_ALL_POSITIONS_ONE_SIDED = 'transferOutAllPositionsOneSided',
  FILTER_TRANSACTIONS_BY_ASSET_ID = 'filterTransactionByAssetId',
  FILTER_TRANSACTIONS_BY_CURRENCY = 'filterTransactionByCurrency',
  EXCHANGE = 'exchange',
  TRANSFER_POSITION = 'transferPosition',
  CREATE_SECURITY_MAPPING = 'createSecurityMapping',
}

export abstract class PositionsService {
  abstract getPositions(
    request: HeliosServerSideRequest<Position, PositionsRequestContext>,
  ): Observable<Position[]>

  abstract getHistoricalBalanceComparisons(
    request: PositionHistoryRequest,
  ): Observable<HistoricalBalanceComparison[]>

  abstract getHistoricalBalanceComparisonsExtToExt(
    request: PositionHistoryExtToExtRequest,
  ): Observable<HistoricalBalanceComparisonExtToExt[]>

  abstract getExternalPositionDetail(
    positionId: number,
  ): Observable<ExternalPositionDetailResponse>

  abstract getPositionComparisons(
    request: ExternalPositionRequest,
  ): Observable<PositionComparisons>

  abstract getPositionComparisonsExtToExt(
    request: ExternalPositionExtToExtRequest,
  ): Observable<PositionComparisonsExtToExt>

  abstract getPositionsByIds(
    positionIds: string[],
    advancedFilterContext: AdvancedFilterContext,
    fields?: string[],
  ): Observable<Position[]>

  abstract getPositionsSetValues(): Observable<PositionsSetValues>
  abstract getPositionsExportExcel(
    request: ExportExcelRequest,
  ): Observable<Blob>

  abstract getPositionsAvailableActions(): Observable<string[]>

  abstract getAvailableRestrictedColumns(): Observable<
    PositionAvailableRestrictedColumns[]
  >
}
